/**
 * This module implements the view handling of drag-and-drop for cards on the backlog.
 */
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import isEmpty from 'lodash/isEmpty';
import {
	type Edge,
	attachClosestEdge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { isSubtaskHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies';
import { useAutobatch } from '@atlassian/jira-software-react-scheduler';
import { createCardGroupDroppableId } from '../../../../common/card-groups';
import { CARD_LIST_DROPPABLE_TYPE } from '../../../../common/constants';
import type { Card, CardId } from '../../../../model/types';
import { CARD_DRAGGABLE_ID_PREFIX } from '../../../../services/card-list-dnd';
import { useBacklogDispatch, useBacklogSelector } from '../../../../state';
import { cardListDragStart, cardListDragEnd } from '../../../../state/actions/card-list';
import { cardDragDisallowed } from '../../../../state/actions/cards/move';
import { beforeCapture, dragEnd, dragStart } from '../../../../state/actions/drag';
import type { CardEntityState } from '../../../../state/reducers/entities/card/types';
import { isCmpKanbanProject } from '../../../../state/selectors/app';
import {
	getCardListsKeys,
	getCards,
	getOrderedCardIdsByCardListId,
	makeGetCardGroupExpanded,
} from '../../../../state/selectors/cards';
import {
	makeIsAllSelectedSiblingSubtasks,
	makeIsAnySelectedOrphanedSubtasks,
} from '../../../../state/selectors/selected-cards';
import { useSelectSingleCard } from '../../../../state/state-hooks/use-select-single-card';
import { useSimplyMoveCards } from '../../../../state/state-hooks/use-simply-move-cards';
import { CardDragPreview } from '../card-drag-preview';

/**
 * Other draggable elements can be dropped into this one, if:
 *
 * * They are the divider, not a card
 * * They are not this card
 * * If the cardList only has a single item.
 * * If the card list has subtasks and the parent is in the list only allow if the next card is not a subtask.
 * * If the card list has sub-tasks, and parent is not in list only allow first and last item to be droppable.
 * * If this is a sub-task, they have the same parent ID as this sub-task
 */
export function getCanDrop({
	args,
	into: { cardId, cardParentId, cardIsSubtask, cardIndex, cardOrderList, cards },
	isAllSelectedSiblingSubtasks,
	isAnySelectedOrphanedSubtask,
}: {
	args: { source: { data: Record<string, unknown> } };
	into: {
		cardId: CardId;
		cardParentId?: CardId | null;
		cardIsSubtask?: boolean;
		cardIndex: number;
		cardOrderList: number[];
		cards: CardEntityState;
	};
	isAllSelectedSiblingSubtasks: boolean;
	isAnySelectedOrphanedSubtask: boolean;
}): boolean {
	if (args.source.data.type === 'DIVIDER') {
		return !cardIsSubtask;
	}

	if (args.source.data.type !== CARD_DRAGGABLE_ID_PREFIX) {
		return false;
	}

	if (args.source.data.cardId === cardId) {
		return false;
	}

	if (!args.source.data.cardIsSubtask && cardOrderList.length === 1) {
		return true;
	}

	// ? Parent is in list and next card is subtask and has same parent
	if (
		cardIsSubtask &&
		args.source.data.cardIsSubtask === false &&
		cardOrderList.find((id) => id === cardParentId)
	) {
		return !(
			isSubtaskHierarchyLevel(cards[cardOrderList[cardIndex + 1]]?.typeHierarchyLevel) &&
			cardParentId === cards[cardOrderList[cardIndex + 1]]?.parentId
		);
	}

	// ? Parent is not in list and prev and next card is subtask
	if (
		cardIsSubtask &&
		args.source.data.cardIsSubtask === false &&
		cardOrderList.find((id) => id === cardParentId) === undefined
	) {
		return !(
			isSubtaskHierarchyLevel(cards[cardOrderList[cardIndex - 1]]?.typeHierarchyLevel) &&
			isSubtaskHierarchyLevel(cards[cardOrderList[cardIndex + 1]]?.typeHierarchyLevel) &&
			cardParentId === cards[cardOrderList[cardIndex - 1]]?.parentId &&
			cardParentId === cards[cardOrderList[cardIndex + 1]]?.parentId
		);
	}

	// rerank within parent card group if only subtasks within that group are selected
	if (cardIsSubtask) {
		return args.source.data.cardParentId === cardParentId && isAllSelectedSiblingSubtasks;
	}

	return !isAnySelectedOrphanedSubtask;
}

export function getCanDrag({
	isCmpKanban,
	isAnySelectedOrphanedSubtask,
	isAllSelectedSiblingSubtasks,
}: {
	isCmpKanban: boolean;
	isAnySelectedOrphanedSubtask: boolean;
	isAllSelectedSiblingSubtasks: boolean;
}): boolean {
	if (isCmpKanban) {
		return true;
	}
	// disable drag if orphaned subtasks have been selected with other cards
	return !(isAnySelectedOrphanedSubtask && !isAllSelectedSiblingSubtasks);
}

/**
 * For non-cmp kanban boards, subtasks cannot be separated from its parent
 *
 * Whenever a user attempts to drag an orphaned subtask
 * we show a helper message explaining why the drag action has been disallowed
 *
 * @param isCmpKanban
 * @param isAnySelectedOrphanedSubtask
 * @param isInvalidDropTarget
 */
export function getShouldShowHelperMessage({
	isCmpKanban,
	isAnySelectedOrphanedSubtask,
	isInvalidDropTarget,
}: {
	isCmpKanban: boolean;
	isAnySelectedOrphanedSubtask: boolean;
	isInvalidDropTarget: boolean;
}): boolean {
	return !isCmpKanban && isAnySelectedOrphanedSubtask && isInvalidDropTarget;
}

interface GetAllowedEdgesParams {
	source: {
		isSubtask: boolean;
		cardId: number;
		parentId: number | null;
	};
	into: {
		index: number;
		isSubtask: boolean;
		cardId: number;
		parentId: number | null;
		isCollapsed: boolean;
	};
	orderedCardIds: number[];
	cards: CardEntityState;
	isAnySelectedOrphanedSubtask: boolean;
	isAllSelectedSiblingSubtasks: boolean;
	isCmpKanban: boolean;
}

/**
 * Work out which edge a card can be dropped on
 *
 * * If dragging a sub-task
 *   * Into ITS parent, allow drop on bottom (unless parent is collapsed)
 *   * Into ITS siblings, allow drop on top and bottom
 *   * Anywhere else, can't drop
 * * If dragging a task
 *   * Into a non-empty parent group, allow drop on top only
 *   * Into a sub-task, allow drop on the first sub-task of the group
 *     if detached from parent
 *   * Into a sub-task, allow drop on the last sub-task of the group
 *   * Into a sibling, allow drop on top/bottom
 */
export function getAllowedEdges({
	source,
	into,
	orderedCardIds,
	cards,
	isAnySelectedOrphanedSubtask,
	isAllSelectedSiblingSubtasks,
	isCmpKanban,
}: GetAllowedEdgesParams): Edge[] {
	const intoCard: Card | undefined = cards[orderedCardIds[into.index]];
	// Cards above and under the *target* card
	const intoCardTop: Card | undefined = cards[orderedCardIds[into.index - 1]];
	const intoCardBottom: Card | undefined = cards[orderedCardIds[into.index + 1]];

	// Dragging sub-task alone or with its own siblings onto its parent group
	if (
		source.isSubtask &&
		!into.isSubtask &&
		source.parentId === intoCard?.id &&
		isAllSelectedSiblingSubtasks
	) {
		return into.isCollapsed ? [] : ['bottom'];
	}

	// Dragging sub-task into any of its siblings
	if (source.isSubtask && into.isSubtask && source.parentId === into.parentId) {
		return ['top', 'bottom'];
	}

	// Can't drag sub-task on other cases if their parent isn't also selected
	if (source.isSubtask && isAnySelectedOrphanedSubtask) {
		return [];
	}

	if (isAnySelectedOrphanedSubtask && !isAllSelectedSiblingSubtasks && !isCmpKanban) {
		return [];
	}

	// Dragging task into a parent group
	const isDroppingIntoParentAttachedToGroup =
		!into.isSubtask && intoCardBottom?.parentId === intoCard?.id && !into.isCollapsed;
	if (isDroppingIntoParentAttachedToGroup) {
		return ['top'];
	}

	// Test function for if a card is the same "card group" as the target
	const isSameGroup = (cardToTest: Card | undefined) => {
		if (cardToTest?.id === into.parentId) {
			return true;
		}
		if (
			cardToTest &&
			isSubtaskHierarchyLevel(cardToTest.typeHierarchyLevel) &&
			cardToTest?.parentId === into.parentId
		) {
			return true;
		}
		return false;
	};

	// Dragging task into a detached sub-task group's first card
	// * target is sub-task
	// * card above it is not or is missing
	// * card above it is not this card's parent
	// This is the "first sub-task in a group without parent"
	const isDetachedSubtaskTopCard = into.isSubtask && !isSameGroup(intoCardTop);
	if (isDetachedSubtaskTopCard) {
		return ['top'];
	}
	// This is the "last sub-task in a group with or without parent"
	const isBottomSubtask = into.isSubtask && !isSameGroup(intoCardBottom);
	if (isBottomSubtask) {
		return ['bottom'];
	}

	// Dragging task into another task
	return into.isSubtask ? [] : ['bottom', 'top'];
}

export interface UseDraggableCardParams {
	elementRef: React.RefObject<HTMLElement>;
	cardId: CardId;
	cardListId: string;
}

export type CardDragState = { type: 'idle' } | { type: 'dragging'; container: HTMLElement };

export interface UseDraggableCardResult {
	isDragging: boolean;
	dragPreview: React.ReactNode;
	closestEdge: Edge | null;
	dropState: {
		cardListIfDivider: string | null;
	} | null;
}

/**
 * Call this hook with the:
 *
 * * Card DOM Element ref
 * * Card ID
 * * Card list ID
 *
 * It will return:
 *
 * * Whether the card is dragging
 * * The drag preview ReactNode, which is actually rendered on a portal into a separate DOM element
 *   before being snapshot by native drag-and-drop
 * * Which edge is closest to the card, if there is another card being dragged into this one
 *
 * Avoid adding too much to this API, consider taking things out of this encapsulated hook and back
 * into the view if that's the case.
 */
export function useDraggableCard({
	elementRef,
	cardId,
	cardListId,
}: UseDraggableCardParams): UseDraggableCardResult {
	const [dragState, setDragState] = useState<CardDragState>({ type: 'idle' });
	const [dropState, setDropState] = useState<{
		edge: Edge;
		cardListIfDivider: string | null;
	} | null>(null);
	const selectSingleCard = useSelectSingleCard();
	const cards = useBacklogSelector(getCards);
	const card = cards[cardId];
	const cardListKeys = useBacklogSelector(getCardListsKeys);
	const dispatch = useBacklogDispatch();
	const cardOrderList = useBacklogSelector(getOrderedCardIdsByCardListId);
	const getIsParentExpanded = useBacklogSelector(makeGetCardGroupExpanded);
	const autobatch = useAutobatch();

	const isAnySelectedOrphanedSubtask: boolean = useBacklogSelector(
		makeIsAnySelectedOrphanedSubtasks,
	);
	const isAllSelectedSiblingSubtasks: boolean = useBacklogSelector(
		makeIsAllSelectedSiblingSubtasks,
	);
	const isCmpKanban = useBacklogSelector(isCmpKanbanProject);

	const moveCards = useSimplyMoveCards();
	useEffect(() => {
		if (!elementRef.current) return undefined;
		if (!card) return undefined;

		const cardParentId = card.parentId;
		const cardIsSubtask = isSubtaskHierarchyLevel(card.typeHierarchyLevel);

		const cardIndex = cardOrderList[cardListId].indexOf(cardId);

		return combine(
			draggable({
				element: elementRef.current,
				getInitialData: () => ({
					type: CARD_DRAGGABLE_ID_PREFIX,
					cardId,
					cardIsSubtask,
					cardParentId,
					cardListId,
				}),
				onDragStart: () => {
					autobatch(() => {
						selectSingleCard(cardId);

						const sourceCardGroupType =
							cardIsSubtask === true && cardListId && cardParentId
								? createCardGroupDroppableId(cardParentId, cardListId)
								: CARD_LIST_DROPPABLE_TYPE;

						dispatch(
							beforeCapture(
								sourceCardGroupType,
								cardListId,
								cardIsSubtask,
								cardParentId ?? undefined,
							),
						);
						dispatch(
							cardListDragStart(
								cardListKeys,
								cardListId,
								cardParentId ? `${cardParentId}-${cardListId}` : undefined,
							),
						);
						dispatch(dragStart());
					});
				},
				onDrop: (_args) => {
					autobatch(() => {
						const isInvalidDropTarget = isEmpty(_args.location.current.dropTargets);
						const showHelperFunction = getShouldShowHelperMessage({
							isCmpKanban,
							isAnySelectedOrphanedSubtask,
							isInvalidDropTarget,
						});
						if (showHelperFunction) {
							dispatch(cardDragDisallowed());
						}
						setDragState({ type: 'idle' });
						dispatch(cardListDragEnd());
					});
				},
				onGenerateDragPreview: ({ nativeSetDragImage }) => {
					setCustomNativeDragPreview({
						getOffset: pointerOutsideOfPreview({
							x: '8px',
							y: '8px',
						}),
						render({ container }) {
							setDragState({ type: 'dragging', container });
							return () => setDragState({ type: 'idle' });
						},
						nativeSetDragImage,
					});
				},
			}),
			dropTargetForElements({
				element: elementRef.current,
				getIsSticky: () => true,
				getData: ({ input, element, source }) => {
					const data = {
						type: CARD_DRAGGABLE_ID_PREFIX,
						cardId,
						cardListId,
					};

					const allowedEdges = getAllowedEdges({
						source: {
							isSubtask: Boolean(source.data.cardIsSubtask),
							cardId: Number(source.data.cardId),
							parentId: source.data.cardParentId != null ? Number(source.data.cardParentId) : null,
						},
						into: {
							index: cardIndex,
							isSubtask: cardIsSubtask,
							cardId,
							parentId: cardParentId,
							isCollapsed: !getIsParentExpanded(cardId),
						},
						orderedCardIds: cardOrderList[cardListId],
						cards,
						isAnySelectedOrphanedSubtask,
						isAllSelectedSiblingSubtasks,
						isCmpKanban,
					});

					return attachClosestEdge(data, {
						input,
						element,
						allowedEdges,
					});
				},
				onDrag: (args) => {
					autobatch(() => {
						if (args.source.data.cardId !== cardId) {
							const edge = extractClosestEdge(args.self.data);
							setDropState(
								edge && {
									edge,
									cardListIfDivider:
										args.source.data.type === 'DIVIDER'
											? String(args.source.data.cardListId)
											: null,
								},
							);
						}
					});
				},
				onDragEnter: (args) => {
					autobatch(() => {
						if (args.source.data.cardId !== cardId) {
							const edge = extractClosestEdge(args.self.data);
							setDropState(
								edge && {
									edge,
									cardListIfDivider:
										args.source.data.type === 'DIVIDER' ? String(args.self.data.cardListId) : null,
								},
							);
						}
					});
				},
				onDragLeave: (_args) => {
					autobatch(() => {
						setDropState(null);
					});
				},
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				onDropTargetChange: (_args) => {},
				canDrop: (args) =>
					getCanDrop({
						args,
						into: {
							cardId,
							cardParentId,
							cardIsSubtask,
							cardIndex,
							cardOrderList: cardOrderList[cardListId],
							cards,
						},
						isAllSelectedSiblingSubtasks,
						isAnySelectedOrphanedSubtask,
					}),
				onDrop: (args) => {
					autobatch(() => {
						const location = {
							type: dropState?.edge === 'top' ? 'before' : 'after',
							target: cardId,
						} as const;
						if (args.source.data.type === 'DIVIDER') {
							moveCards({
								source: {
									type: 'divider',
									cardListId: String(args.source.data.cardListId),
								},
								location,
							});
						}

						if (args.source.data.type === CARD_DRAGGABLE_ID_PREFIX) {
							moveCards({
								source: {
									cardId: Number(args.source.data.cardId),
								},
								location,
							});
						}

						setDropState(null);

						dispatch(dragEnd());
					});
				},
			}),
		);
	}, [
		card,
		cardId,
		cardListId,
		dispatch,
		moveCards,
		dropState?.edge,
		elementRef,
		selectSingleCard,
		cardListKeys,
		cardOrderList,
		cards,
		getIsParentExpanded,
		autobatch,
		isAnySelectedOrphanedSubtask,
		isAllSelectedSiblingSubtasks,
		isCmpKanban,
	]);

	return {
		isDragging: dragState.type === 'dragging',
		dragPreview:
			dragState.type === 'dragging'
				? ReactDOM.createPortal(<CardDragPreview id={cardId} />, dragState.container)
				: null,
		closestEdge: dropState?.edge ?? null,
		dropState,
	};
}
